<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <import-page-banner
                title="Marketplace Order"
                sample-csv-path="/createMarketplaceOrderWithItemsByImportSample.csv"
            />

            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6"> Create Marketplace Order by Import </v-col>
                        <v-col cols="12" sm="12" md="6"> </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <p><b>Only accept csv file.</b></p>
                    <p>1. Choose your file</p>
                    <p>2. SELECT THE CORRECT MAPPING OF Field and Column</p>
                    <p>3. IMPORT</p>
                    <p>
                        <master-data-reference-button
                            button-label="Marketplace List"
                            dialog-title="Valid Marketplace Codes"
                            :items="marketplaceOptions"
                        />
                        <master-data-reference-button
                            button-label="Currency List"
                            dialog-title="Valid Currency Codes"
                            :items="currencyOptions"
                        />
                    </p>
                    <!-- New CSV Importer Component -->
                    <csv-importer
                        :field-definitions="data.fields"
                        :preview-headers="data.previewHeaders"
                        @data-loaded="onCsvDataLoaded"
                        @data-cleared="onCsvDataCleared"
                    />
                </v-card-text>
            </v-card>
            <br />
            <v-card v-if="data.mappedCsv && data.mappedCsv.length > 0">
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="12">
                            <v-btn
                                class="mr-2"
                                color="primary"
                                :loading="loading"
                                :disabled="loading || invalidFile"
                                @click="onImportClick"
                            >
                                <v-icon>mdi-cloud-upload</v-icon>
                                Import
                            </v-btn>
                            <v-btn class="mr-2" :to="pathListMarketplaceOrder"> Back to list </v-btn>
                            <span v-if="importDone">{{ importDoneMsg }} </span>
                        </v-col>
                    </v-row>
                </v-card-title>
                <v-card-text>
                    <v-alert v-if="importDone && orderStatusSummary.length > 0" type="info" class="mb-4">
                        <strong>Import Summary:</strong>
                        <ul>
                            <li v-for="orderStatus in orderStatusSummary" :key="orderStatus.key">
                                <strong>{{ orderStatus.orderNumber }}</strong> ({{ orderStatus.marketplaceCode }}):
                                <v-chip :color="orderStatus.status === 'success' ? 'success' : 'error'" size="small">
                                    {{ orderStatus.status }}
                                </v-chip>
                                <span v-if="orderStatus.error" class="ml-2 text-error">{{ orderStatus.error }}</span>
                            </li>
                        </ul>
                    </v-alert>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(item, idx) in transformMappedCsv(data.mappedCsv)"
                                :key="`row${idx}`"
                                :class="invalidRowClass(item)"
                            >
                                <td v-for="header in data.headers" :key="header.value">
                                    {{ item[header.value] }}
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { fetchWrapper } from '../../utils/FetchWrapper';
import * as _ from 'lodash';
import { PathDefinition } from '../../utils/PathDefinition';
import { ImportTableLabelTextHelper } from '../../utils/labelTextHelper/ImportTableLabelTextHelper';
import { SharedMsgHelper } from '../../utils/SharedMsgHelper';
import { ValidationHelper } from '../../utils/ValidationHelper';
import { Formatter } from '../../utils/Formatter';
import { MasterDataApiCaller } from '../../utils/MasterDataApiCaller';
import { MarketplaceOrderHeaderHelper } from '../../utils/headerHelper/MarketplaceOrderHeaderHelper';
import { ImportHeaderHelper } from '../../utils/ImportHeaderHelper';
import { MarketplaceOrderItemHeaderHelper } from '../../utils/headerHelper/MarketplaceOrderItemHeaderHelper';
import { useMasterData } from '../../composables/useMasterData';
import MasterDataReferenceButton from '../../components/MasterDataReferenceButton.vue';
import CsvImporter from '../../components/CsvImporter.vue';
import ImportPageBanner from '../../components/ImportPageBanner.vue';
import { MarketplaceOrderImportValidation } from '../../config/importValidationConfigs';
import { ImportValidationHelper } from '../../utils/ImportValidationHelper';

export default defineComponent({
    name: 'ImportMarketplaceOrder',

    components: {
        MasterDataReferenceButton,
        CsvImporter,
        ImportPageBanner
    },

    setup() {
        // Load master data options for reference
        const { marketplaceOptions, currencyOptions, loadAllMasterData } = useMasterData();

        const data = reactive({
            mappedCsv: null,
            fields: {
                ...MarketplaceOrderHeaderHelper.getImportFields(),
                ...MarketplaceOrderItemHeaderHelper.getImportWithItemFields()
            },
            previewHeaders: [
                ...MarketplaceOrderHeaderHelper.getImportHeaders(),
                ...MarketplaceOrderItemHeaderHelper.getImportWithItemHeaders()
            ],
            headers: [
                ...ImportHeaderHelper.getHeaders(),
                ...MarketplaceOrderHeaderHelper.getImportHeaders(),
                ...MarketplaceOrderItemHeaderHelper.getImportWithItemHeaders()
            ]
        });

        onMounted(async () => {
            // Load master data for reference button
            await loadAllMasterData();
        });

        return {
            data,
            marketplaceOptions,
            currencyOptions
        };
    },
    data() {
        return {
            loading: false,
            invalidFile: false,
            importDone: false,
            importDoneMsg: SharedMsgHelper.getImportDoneMsg,
            pathListMarketplaceOrder: PathDefinition.listMarketplaceOrder
        };
    },
    computed: {
        orderStatusSummary() {
            if (!this.data.mappedCsv || this.data.mappedCsv.length === 0) {
                return [];
            }

            // Group by order (orderNumber + marketplaceCode)
            const orderGroups = _.groupBy(this.data.mappedCsv, (item) => `${item.orderNumber}|${item.marketplaceCode}`);

            return _.map(orderGroups, (items, key) => {
                const firstItem = items[0];
                return {
                    key: key,
                    orderNumber: firstItem.orderNumber,
                    marketplaceCode: firstItem.marketplaceCode,
                    status: firstItem.status || 'pending',
                    error: firstItem.error || null
                };
            });
        }
    },
    async created() {},
    methods: {
        onCsvDataLoaded(mappedData) {
            // Receive data from CSV importer component
            this.data.mappedCsv = mappedData;
        },
        onCsvDataCleared() {
            // Clear data when file is removed
            this.data.mappedCsv = null;
            this.importDone = false;
            this.invalidFile = false;
        },
        transformMappedCsv(mappedCsv) {
            const importHeaderValues = this.data.headers.map((header) => header.value);
            return ImportValidationHelper.validateRows(mappedCsv, MarketplaceOrderImportValidation, importHeaderValues);
        },
        groupDataByOrderKey(mappedCsv) {
            const orderFields = [
                'dateOrderOn',
                'orderNumber',
                'marketplaceCode',
                'orderAmount',
                'orderCurrencyCode',
                'commissionPercentage',
                'transactionFee',
                'remarks'
            ];
            const itemFields = ['sku', 'quantity', 'itemUnitPriceAmount'];

            // Create composite key: orderNumber + marketplaceCode
            const groupByCompositeKey = (item) => `${item.orderNumber}|${item.marketplaceCode}`;

            return _.mapValues(_.groupBy(mappedCsv, groupByCompositeKey), (group) => {
                const orderInfo = _.pick(group[0], [...orderFields, 'status', 'error']);
                const items = group.map((item) => _.pick(item, [...itemFields, 'status', 'error']));

                return {
                    orderInfo,
                    items,
                    orderNumber: group[0].orderNumber,
                    marketplaceCode: group[0].marketplaceCode,
                    compositeKey: groupByCompositeKey(group[0])
                };
            });
        },
        validateGroupConsistency(groupedData) {
            const orderFields = [
                'dateOrderOn',
                'orderAmount',
                'orderCurrencyCode',
                'commissionPercentage',
                'transactionFee',
                'remarks'
            ];

            for (const compositeKey in groupedData) {
                const group = groupedData[compositeKey];
                const originalData = this.data.mappedCsv.filter(
                    (item) => item.orderNumber === group.orderNumber && item.marketplaceCode === group.marketplaceCode
                );

                // Check if all order information is consistent across rows with same orderNumber + marketplaceCode
                for (let i = 1; i < originalData.length; i++) {
                    for (const field of orderFields) {
                        if (originalData[0][field] !== originalData[i][field]) {
                            // Mark inconsistent rows with error
                            originalData[i].status = ImportTableLabelTextHelper.getImportResultValue.error;
                            originalData[
                                i
                            ].error = `Inconsistent ${field} for order ${group.orderNumber} in ${group.marketplaceCode}`;
                        }
                    }
                }
            }
        },
        async onImportClick() {
            this.data.mappedCsv = this.transformMappedCsv(this.data.mappedCsv);
            if (this.data.mappedCsv && this.data.mappedCsv.length > 0) {
                await this.importSubmit();
            } else {
                console.log('empty csv');
            }
        },
        async importSubmit() {
            this.loading = true;

            // Group data by order key (orderNumber + marketplaceCode) and validate consistency
            const groupedData = this.groupDataByOrderKey(this.data.mappedCsv);
            this.validateGroupConsistency(groupedData);

            const isValid = this.validation(this.data.mappedCsv);
            if (isValid) {
                // Process each order group
                for (const compositeKey in groupedData) {
                    await this.createOrderWithItems(groupedData[compositeKey]);
                }
                this.importDone = true;
                // prevent import again
                this.invalidFile = true;
            } else {
                console.log('invalid csv');
            }
            this.loading = false;
        },
        validation(list) {
            return _.every(list, (v) => {
                return !this.isInvalidRow(v);
            });
        },
        invalidRowClass(item) {
            if (this.isInvalidRow(item)) {
                return 'row-validation-error';
            }
            return '';
        },
        isInvalidRow(item) {
            return (
                this.isInvalidRowContent(item) || item.status === ImportTableLabelTextHelper.getImportResultValue.error
            );
        },
        isInvalidRowContent(item) {
            return ImportValidationHelper.hasValidationErrors(item, MarketplaceOrderImportValidation);
        },
        async createOrderWithItems(orderGroup) {
            const { orderNumber, marketplaceCode } = orderGroup;

            try {
                // Create the order with all items in a single atomic transaction
                const orderResponse = await this.createMarketplaceOrder(orderGroup);

                if (orderResponse.success) {
                    // Mark all rows for this order as success
                    this.updateOrderRowsStatus(
                        orderNumber,
                        marketplaceCode,
                        ImportTableLabelTextHelper.getImportResultValue.success,
                        null
                    );
                } else {
                    // If order creation fails, mark all rows for this order as error
                    this.updateOrderRowsStatus(
                        orderNumber,
                        marketplaceCode,
                        ImportTableLabelTextHelper.getImportResultValue.error,
                        orderResponse.errors
                    );
                }
            } catch (error) {
                console.error('Error creating order with items:', error);
                this.updateOrderRowsStatus(
                    orderNumber,
                    marketplaceCode,
                    ImportTableLabelTextHelper.getImportResultValue.error,
                    error.message || 'Unknown error'
                );
            }
        },
        async createMarketplaceOrder(orderGroup) {
            const { orderInfo, items } = orderGroup;
            const orderFields = [
                'dateOrderOn',
                'orderNumber',
                'marketplaceCode',
                'orderAmount',
                'orderCurrencyCode',
                'commissionPercentage',
                'transactionFee',
                'remarks'
            ];
            const request = {
                ..._.pick(orderInfo, orderFields),
                orderItems: items.map((item) => ({
                    sku: item.sku,
                    quantity: item.quantity,
                    itemUnitPriceAmount: item.itemUnitPriceAmount
                }))
            };
            const requestUrl = `/api/staff/marketplaceOrder/create`;
            return await fetchWrapper.post(requestUrl, request);
        },
        updateOrderRowsStatus(orderNumber, marketplaceCode, status, error) {
            for (let i = 0; i < this.data.mappedCsv.length; i++) {
                if (
                    this.data.mappedCsv[i].orderNumber === orderNumber &&
                    this.data.mappedCsv[i].marketplaceCode === marketplaceCode
                ) {
                    this.data.mappedCsv[i].status = status;
                    if (error) {
                        this.data.mappedCsv[i].error = error;
                    }
                }
            }
        }
    }
});
</script>

<style>
select {
    border: 1px solid !important;
}

.row-validation-error {
    background: red;
}
</style>
